import { useEffect, useRef } from 'react';

const FEATURES = [
  { icon: 'ri-drop-line', ko: 'FGF 성장인자', en: 'Fibroblast Growth Factor', desc: '피부 세포 재생을 촉진해 탄력과 광채를 회복시킵니다.' },
  { icon: 'ri-sun-line', ko: 'EGF 상피세포인자', en: 'Epidermal Growth Factor', desc: '표피세포 활성화로 잔주름 개선과 피부 결 정돈 효과.' },
  { icon: 'ri-water-flash-line', ko: '물광 피부', en: 'Glass Skin Effect', desc: '미세 제트 분사로 피부 깊숙이 유효 성분을 침투, 촉촉한 광채 피부 완성.' },
  { icon: 'ri-flashlight-line', ko: '역노화 관리', en: 'Reverse Aging Care', desc: '시간을 되돌리듯 젊고 건강한 피부 상태로 되돌아갑니다.' },
];

export default function AntiAgingSection() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1 }
    );
    const els = ref.current?.querySelectorAll('.section-reveal, .section-reveal-left, .section-reveal-right, .section-reveal-scale');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="anti-aging" ref={ref} className="bg-cream py-20 px-7 md:py-28 md:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14 md:mb-20 section-reveal">
          <span className="inline-block text-gold font-sans text-xs sm:text-sm font-semibold tracking-widest uppercase mb-3">
            Anti-Aging &amp; Laser · 抗衰老激光 · アンチエイジングレーザー
          </span>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-gray-900 font-bold mb-4">안티에이징 &amp; 레이저</h2>
          <div className="w-16 h-0.5 bg-gold mx-auto mb-5"></div>
          <p className="font-sans text-gray-500 max-w-xl mx-auto text-sm sm:text-base">
            최첨단 MICRO-JET 레이저 기술로 시간의 흔적을 지우고, 젊고 빛나는 피부를 되찾으세요.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-14 md:gap-18 items-center">
          {/* Left Image — slides in from left */}
          <div className="section-reveal-left">
            <div className="relative rounded-2xl overflow-hidden">
              <div className="w-full h-64 sm:h-80 md:h-[500px]">
                <img
                  src="https://public.readdy.ai/ai/img_res/53c02c2ecc135ad02abce22e77c89bb0.jpg"
                  alt="역노화 레이저 시술"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-plum-dark/40 via-transparent to-transparent"></div>
              <div className="absolute bottom-5 left-5 right-5">
                <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4">
                  <p className="font-serif text-sm font-bold text-gray-900">MICRO-JET Technology</p>
                  <p className="font-sans text-xs text-plum mt-0.5">FGF + EGF 물광 피부 솔루션</p>
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {['FGF', 'EGF', 'MICRO-JET', '물광피부'].map((tag) => (
                      <span key={tag} className="bg-cream border border-gold/30 text-gold-dark text-xs px-2 py-0.5 rounded-full font-sans">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content — slides in from right */}
          <div className="section-reveal-right">
            <div className="flex items-center gap-2 mb-4">
              <span className="bg-plum text-white text-xs px-3 py-1 rounded-full font-sans font-semibold whitespace-nowrap">MICRO-JET</span>
              <span className="bg-gold text-white text-xs px-3 py-1 rounded-full font-sans font-semibold whitespace-nowrap">역노화 레이저</span>
            </div>
            <h3 className="font-serif text-2xl md:text-3xl text-gray-900 font-bold mb-3">
              Reverse Aging Laser<br />
              <span className="text-plum text-xl">逆衰老激光 · 逆エイジングレーザー</span>
            </h3>
            <p className="font-sans text-gray-600 text-base leading-relaxed mb-8">
              헤라브로우클리닉의 <strong>MICRO-JET</strong> 기술은 FGF(섬유아세포 성장인자)와 EGF(상피세포 성장인자)를 피부 깊숙이 침투시켜 세포 단위의 역노화를 실현합니다. 단 한 번의 시술로도 눈에 띄는 광채와 탄력 개선 효과를 경험할 수 있습니다.
            </p>

            {/* Feature Grid — staggered */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {FEATURES.map((f, i) => (
                <div
                  key={f.ko}
                  className="section-reveal-scale bg-white border border-gold/15 rounded-xl p-4 hover:border-gold/40 hover:-translate-y-1 transition-all duration-300"
                  style={{ transitionDelay: `${0.08 + i * 0.11}s` }}
                >
                  <div className="w-8 h-8 flex items-center justify-center text-gold mb-2">
                    <i className={`${f.icon} text-xl`}></i>
                  </div>
                  <p className="font-serif text-sm font-bold text-gray-900 mb-0.5">{f.ko}</p>
                  <p className="font-sans text-xs text-plum mb-1">{f.en}</p>
                  <p className="font-sans text-xs text-gray-500 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>

            {/* Price Highlight */}
            <div className="bg-gradient-to-r from-plum to-plum-light rounded-2xl p-5 text-white flex items-center justify-between gap-3">
              <div>
                <p className="font-sans text-sm text-white/70 mb-0.5">이벤트 특가 · Event Price</p>
                <p className="font-serif text-2xl font-bold">250,000원</p>
                <p className="font-sans text-xs text-white/60 mt-1">MICRO-JET (FGF+EGF)</p>
              </div>
              <button
                onClick={() => { const el = document.querySelector('#reservation'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
                className="flex-shrink-0 bg-gold text-white px-4 sm:px-5 py-2.5 rounded-full text-sm font-sans font-semibold whitespace-nowrap cursor-pointer hover:bg-gold-dark transition-colors duration-200"
              >
                예약하기
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
